import { formatPhoneNumber } from '@supply-mover-2-0/core';
import { ClickToDialPhone } from '../../../components/display/ClickToDialPhone';
import { resolveThemeColor } from '../../../constants/theme-aware-colors';
import { useTheme, useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import type { FieldConfiguration, TableRow } from '../../types';
import { getFieldDisplayProperties } from './displayHelpers';

interface PhoneDisplayProps {
	field: FieldConfiguration;
	value: unknown;
	data: TableRow;
}

export function PhoneDisplay({ field, value, data }: PhoneDisplayProps) {
	const colors = useThemeColors();
	const { isDark } = useTheme();
	const { t } = useTranslation();
	const { dataConfig } = field;

	// Get display value and color using value mapping
	const { displayValue, colorValue } = getFieldDisplayProperties(
		field,
		value,
		data,
	);
	const textColor =
		resolveThemeColor(colorValue?.text, isDark) ?? colors.values.textPrimary;

	if (value == null && dataConfig?.defaultValue == null) {
		return null;
	}

	// Check if the value is the default value
	const isDefaultValue = value === dataConfig?.defaultValue;

	// For phone numbers, we format the original value but can use mapped display text if provided
	const formattedPhone =
		displayValue !== value
			? displayValue
			: value != null
				? formatPhoneNumber(value as string)
				: (dataConfig?.defaultValue ?? '');

	// Don't render as link if it's the default value or if links are disabled
	if (field.link?.enabled !== false && !isDefaultValue && value != null) {
		return <ClickToDialPhone phoneNumber={value as string} />;
	}

	return <span style={{ color: textColor }}>{t(String(formattedPhone))}</span>;
}
